"use client";

import React, { useState, useRef, useEffect } from "react";
import styles from "./CustomSelect.module.css";
import { FiArrowDown, FiChevronDown } from "react-icons/fi";
import { ArrowDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  placeholder = "Select an option",
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLLIElement | null>(null);

 
  const handleSelect = (option: Option) => {
    if (!disabled) {
      const syntheticEvent = {
        target: { name, value: option.value },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
      setIsOpen(false);
    }
  };

  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      setIsOpen((prev) => !prev);
      event.preventDefault();
    } else if (event.key === "Escape") {
      setIsOpen(false);
      event.preventDefault();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (isOpen) {
        const currentIndex = options.findIndex((opt) => opt.value === value);
        let nextIndex = event.key === "ArrowDown" ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0) nextIndex = options.length - 1;
        if (nextIndex >= options.length) nextIndex = 0;

        const syntheticEvent = {
          target: { name, value: options[nextIndex].value },
        } as unknown as React.ChangeEvent<HTMLSelectElement>;

        onChange(syntheticEvent);
        selectedOptionRef.current?.focus();
      } else {
        setIsOpen(true);
      }
      event.preventDefault();
    }
  };

  return (
    <div className={styles.formGroup} ref={dropdownRef}>
      <label htmlFor={name} className={styles.formlabel}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>

      <div
        className={`${styles.formselect} 
          ${error ? styles.error : ""} 
          ${isOpen ? styles.focus : ""} 
          ${disabled ? styles.disabled : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={!!isOpen}          
        aria-controls={`${name}-dropdown`}
        aria-haspopup="listbox"           
        aria-labelledby={`${name}-label`} 
        id={name}
        title={label}                    
      >
        {value
          ? options.find((opt) => opt.value === value)?.label
          : placeholder}
                <FiChevronDown  className={styles.downarrow}/>

      </div>

      {isOpen && (
        <ul
          className={styles.dropdown}
          id={`${name}-dropdown`}
          role="listbox"
          aria-label={`${label} options`}
        >
          {options.map((option) => (
            <li
              key={option.value}
              className={`${styles.formoption} ${value === option.value ? styles.selected : ""}`}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={value === option.value}
              ref={value === option.value ? selectedOptionRef : null}
              tabIndex={-1}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

export default CustomSelect;
